import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { Download, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { dspAPI, saveBlob } from '../../lib/dspAPI';
import { errMsg } from '../../lib/atoAPI';
import { SimpleTable } from '../ato/AtoBits';
import { RegisterView } from './RegisterView';

const SECTIONS = [
  ['dsp_registration', 'DSP Registration Status'], ['osf', 'Operational Security Framework'], ['osf_questionnaire', 'OSF Questionnaire'],
  ['controls', 'Security Controls Register'], ['evidence', 'Evidence Repository'], ['risks', 'Risk Register'], ['incidents', 'Security Incidents'],
  ['residency', 'Data Residency Assessment'], ['architecture', 'Infrastructure Architecture'], ['suppliers', 'Supplier Register'],
  ['pentests', 'Penetration Testing'], ['certification', 'ATO Service Certification'],
  ['approvals', 'Production Approval Register'], ['outstanding', 'Outstanding Compliance Actions'],
];
const OSF_AREAS = ['Audit logging', 'Authentication & MFA', 'Certification', 'Data hosting', 'Encryption key management', 'Encryption at rest',
  'Encryption in transit', 'Entity validation', 'Personnel security', 'Security monitoring', 'Supply chain'];

const Breakdown = ({ meta }) => (
  <div className="flex flex-wrap gap-2 text-xs" data-testid="dsp-status-breakdown">
    {Object.entries(meta?.status_breakdown || {}).map(([k, v]) => <span key={k} className="rounded-full border px-2 py-0.5">{k}: <b>{v}</b></span>)}
  </div>
);

const Docs = ({ api }) => {
  const [docs, setDocs] = useState([]);
  useEffect(() => { Promise.all(['04-target-architecture.md', '08-ato-gateway-architecture.md'].map((n) => api.doc(n))).then(setDocs).catch((e) => toast.error(errMsg(e))); }, [api]);
  return docs.map((d) => <article key={d.name} className="space-y-2 rounded-md border p-4 text-sm [&_h1]:text-xl [&_h1]:font-semibold [&_h2]:mt-4 [&_h2]:font-semibold [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-3 [&_pre]:text-xs [&_ul]:list-disc [&_ul]:pl-5" data-testid={`dsp-doc-${d.name}`}><ReactMarkdown>{d.markdown}</ReactMarkdown></article>);
};

const Evidence = ({ api, canEdit }) => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: '', control_ids: '', owner: '', date: '', classification: 'Internal', link: '', osf_area: '' });
  const [file, setFile] = useState(null);
  const load = () => api.evidence().then((d) => setRows(d.items)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const add = async () => {
    const fd = new FormData(); Object.entries(f).forEach(([k, v]) => fd.append(k, v)); if (file) fd.append('file', file);
    try { await api.addEvidence(fd); toast.success('Evidence added'); setF({ ...f, title: '', link: '' }); setFile(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const dl = async (r) => { try { saveBlob(await api.evidenceBlob(r.id), r.filename); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-3">
      {canEdit && <div className="grid gap-2 rounded-md border p-3 md:grid-cols-3" data-testid="dsp-evidence-form">
        {['title', 'control_ids', 'owner', 'classification', 'link'].map((k) => <Input key={k} placeholder={k.replace('_', ' ')} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`dsp-evidence-${k}`} />)}
        <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="dsp-evidence-date" />
        <select className="h-9 rounded-md border bg-background px-2 text-sm" value={f.osf_area || ''} onChange={(e) => setF({ ...f, osf_area: e.target.value })} data-testid="dsp-evidence-osf-area">
          <option value="">OSF area (optional)</option>
          {OSF_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="dsp-evidence-file" />
        <Button onClick={add} disabled={!f.title || (!file && !f.link)} data-testid="dsp-evidence-add">Add evidence (virus-scanned)</Button></div>}
      <SimpleTable testId="dsp-evidence-table" rows={rows} empty="No evidence yet." cols={[{ k: 'title', label: 'Title' }, { k: 'control_ids', label: 'Controls' }, { k: 'date', label: 'Date' },
        { k: 'owner', label: 'Owner' }, { k: 'classification', label: 'Class' },
        { k: 'x', label: 'Item', render: (r) => (r.file_id ? <Button size="sm" variant="ghost" onClick={() => dl(r)} disabled={r.scan_status !== 'clean'}>{r.filename} ({r.scan_status})</Button> : <a href={r.link} target="_blank" rel="noreferrer" className="text-primary hover:underline">link</a>) }]} />
    </div>
  );
};

const Approvals = ({ api, org }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (!api.isPlatform || org) api.gates(org).then((d) => setRows(d.items)).catch((e) => toast.error(errMsg(e))); }, [api, org]);
  return <SimpleTable testId="dsp-approvals" rows={rows} empty="Select a practice." cols={[{ k: 'service_id', label: 'Service' },
    { k: 'e', label: 'EVTE', render: (r) => (r.evte_unmet.length ? <span className="text-rose-700">Not granted</span> : 'Granted') },
    { k: 'p', label: 'Production', render: (r) => (r.production_unmet.length ? <span className="text-rose-700">{r.production_unmet.length} unmet item(s)</span> : 'All items confirmed') }]} />;
};

const Outstanding = ({ api }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.outstanding().then((d) => setRows(d.items)).catch((e) => toast.error(errMsg(e))); }, [api]);
  return <SimpleTable testId="dsp-outstanding" rows={rows} cols={[{ k: 'register', label: 'Register' }, { k: 'item', label: 'Item' }, { k: 'status', label: 'Status' }, { k: 'action', label: 'Action' }]} />;
};

export const DspCompliance = ({ platform = false, org }) => {
  const api = useMemo(() => ({ ...dspAPI(platform), isPlatform: platform }), [platform]);
  const [sec, setSec] = useState('controls');
  const [meta, setMeta] = useState(null);
  useEffect(() => { api.registers().then(setMeta).catch((e) => toast.error(errMsg(e))); }, [api, sec]);
  const pack = async () => { try { saveBlob(await api.pack(), 'dsp-evidence-pack.zip'); toast.success('Evidence pack generated'); } catch (e) { toast.error(errMsg(e)); } };
  const canEdit = !!meta?.can_edit;
  const regMeta = meta?.registers?.[sec];
  return (
    <div className="space-y-4" data-testid="dsp-compliance">
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-sky-200 bg-sky-50 px-4 py-2.5 text-sm text-sky-900" data-testid="dsp-disclaimer">
        <Info className="h-4 w-4" />Internal tracking only. Not ATO approval or certification.
        <Button size="sm" variant="outline" className="ml-auto" onClick={pack} data-testid="dsp-evidence-pack"><Download className="mr-1 h-4 w-4" />Evidence pack (ZIP)</Button>
      </div>
      <div className="grid gap-4 md:grid-cols-[230px_1fr]">
        <nav className="flex flex-col gap-0.5" data-testid="dsp-sections">{SECTIONS.map(([k, t]) => (
          <button key={k} onClick={() => setSec(k)} data-testid={`dsp-sec-${k}`}
            className={cn('rounded px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted', sec === k && 'bg-muted font-medium')}>{t}</button>))}</nav>
        <section className="min-w-0 space-y-3">
          <h2 className="text-lg font-semibold">{SECTIONS.find(([k]) => k === sec)[1]}</h2>
          {regMeta && <Breakdown meta={regMeta} />}
          {regMeta && <RegisterView key={sec} reg={sec} api={api} canEdit={canEdit} />}
          {sec === 'osf' && <div className="space-y-2 text-sm"><p>The ATO’s Operational Security Framework (OSF) sets security requirements for digital service providers that connect to ATO services. Requirements depend on the product category and must be confirmed with the ATO.</p>
            <a className="text-primary hover:underline" href={meta?.osf_url} target="_blank" rel="noreferrer">Official OSF guidance (ATO)</a>
            <p className="text-muted-foreground">Track answers in “OSF Questionnaire” and control status in “Security Controls Register”.</p></div>}
          {sec === 'evidence' && <Evidence api={api} canEdit={canEdit} />}
          {sec === 'architecture' && <Docs api={api} />}
          {sec === 'approvals' && <Approvals api={api} org={org} />}
          {sec === 'outstanding' && <Outstanding api={api} />}
        </section>
      </div>
    </div>
  );
};
